import {
  ApiDurationMove,
  ApiWorkoutMove,
  ApiWorkoutMoveSet,
  FinalDatetimeListItem,
  FinalProgrammePlan,
  GenerateProgramResponse,
} from './types';
import { ExerciseData, ProgramData } from './useProgramDataTransformer';

export interface TransformPlanOptions {
  duration?: number | string;
  daysPerWeek?: number;
  selectedDays?: string[];
  reminderTime?: string;
  isManually?: boolean;
}

interface DayConfig {
  name: string;
  shortName: string;
  dayNum: number; // 1 = Monday ... 7 = Sunday
  offsetFromMonday: number; // 0 = Mon, 1 = Tue, 2 = Wed, 3 = Thu, 4 = Fri, 5 = Sat, 6 = Sun
}

const DAY_CONFIG_MAP: Record<string, DayConfig> = {
  monday: { name: 'Monday', shortName: 'Mon', dayNum: 1, offsetFromMonday: 0 },
  mon: { name: 'Monday', shortName: 'Mon', dayNum: 1, offsetFromMonday: 0 },
  m: { name: 'Monday', shortName: 'Mon', dayNum: 1, offsetFromMonday: 0 },
  tuesday: { name: 'Tuesday', shortName: 'Tue', dayNum: 2, offsetFromMonday: 1 },
  tue: { name: 'Tuesday', shortName: 'Tue', dayNum: 2, offsetFromMonday: 1 },
  t: { name: 'Tuesday', shortName: 'Tue', dayNum: 2, offsetFromMonday: 1 },
  wednesday: { name: 'Wednesday', shortName: 'Wed', dayNum: 3, offsetFromMonday: 2 },
  wed: { name: 'Wednesday', shortName: 'Wed', dayNum: 3, offsetFromMonday: 2 },
  we: { name: 'Wednesday', shortName: 'Wed', dayNum: 3, offsetFromMonday: 2 },
  w: { name: 'Wednesday', shortName: 'Wed', dayNum: 3, offsetFromMonday: 2 },
  thursday: { name: 'Thursday', shortName: 'Thu', dayNum: 4, offsetFromMonday: 3 },
  thu: { name: 'Thursday', shortName: 'Thu', dayNum: 4, offsetFromMonday: 3 },
  th: { name: 'Thursday', shortName: 'Thu', dayNum: 4, offsetFromMonday: 3 },
  friday: { name: 'Friday', shortName: 'Fri', dayNum: 5, offsetFromMonday: 4 },
  fri: { name: 'Friday', shortName: 'Fri', dayNum: 5, offsetFromMonday: 4 },
  f: { name: 'Friday', shortName: 'Fri', dayNum: 5, offsetFromMonday: 4 },
  saturday: { name: 'Saturday', shortName: 'Sat', dayNum: 6, offsetFromMonday: 5 },
  sat: { name: 'Saturday', shortName: 'Sat', dayNum: 6, offsetFromMonday: 5 },
  sa: { name: 'Saturday', shortName: 'Sat', dayNum: 6, offsetFromMonday: 5 },
  s: { name: 'Saturday', shortName: 'Sat', dayNum: 6, offsetFromMonday: 5 },
  sunday: { name: 'Sunday', shortName: 'Sun', dayNum: 7, offsetFromMonday: 6 },
  sun: { name: 'Sunday', shortName: 'Sun', dayNum: 7, offsetFromMonday: 6 },
  su: { name: 'Sunday', shortName: 'Sun', dayNum: 7, offsetFromMonday: 6 },
};

const DEFAULT_CONFIGS: DayConfig[] = [
  DAY_CONFIG_MAP.monday,
  DAY_CONFIG_MAP.tuesday,
  DAY_CONFIG_MAP.wednesday,
  DAY_CONFIG_MAP.thursday,
  DAY_CONFIG_MAP.friday,
  DAY_CONFIG_MAP.saturday,
  DAY_CONFIG_MAP.sunday,
];

const DEFAULT_OFFSETS_BY_DAYS_COUNT: Record<number, number[]> = {
  1: [0], // Mon
  2: [0, 3], // Mon, Thu
  3: [0, 2, 4], // Mon, Wed, Fri
  4: [0, 2, 4, 5], // Mon, Wed, Fri, Sat
  5: [0, 1, 2, 3, 4], // Mon-Fri
  6: [0, 1, 2, 3, 4, 5], // Mon-Sat
  7: [0, 1, 2, 3, 4, 5, 6], // Mon-Sun
};

const getStartMonday = (baseDate: Date): Date => {
  const d = new Date(baseDate);
  const day = d.getDay(); // 0 is Sunday, 1 is Monday ... 6 is Saturday
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.getFullYear(), d.getMonth(), diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

export const transformProgramStateToPlan = (
  programData: ProgramData,
  rawApiResponse: GenerateProgramResponse,
  options?: TransformPlanOptions,
): FinalProgrammePlan | null => {
  const rawData =
    rawApiResponse?.program?.data ||
    rawApiResponse?.result?.program?.data ||
    (rawApiResponse as any)?.data ||
    (rawApiResponse as any)?.result ||
    rawApiResponse ||
    {};

  // 1. Resolve duration in weeks
  const rawDraft = (rawData as Record<string, unknown>).draft as
    | { progress?: Record<string, Array<{ slug?: string; value?: string }>> }
    | undefined;

  const rawDuration =
    options?.duration ??
    rawData.duration ??
    rawDraft?.progress?.program_duration?.[0]?.value ??
    rawDraft?.progress?.programDuration?.[0]?.value;
  const totalWeeks = rawDuration ? parseInt(String(rawDuration).replace(/\D/g, ''), 10) || 1 : 1;

  // 2. Existing sorted api days (if AI generated or previously saved)
  const sortedApiDays = [...(rawData.datetimeList || [])].sort(
    (a, b) => new Date(a.ymd).getTime() - new Date(b.ymd).getTime(),
  );

  // 3. Resolve selected schedule days (e.g. ['monday', 'wednesday', 'friday', 'saturday'])
  const rawSchedule: unknown[] =
    options?.selectedDays ??
    ((rawData as Record<string, unknown>).selectedDays as unknown[]) ??
    ((rawData as Record<string, unknown>).selected_days as unknown[]) ??
    (rawDraft?.progress?.how_often_train || []).map((item) => item.slug || item.value) ??
    (rawDraft?.progress?.howOftenTrain || []).map((item) => item.slug || item.value);

  const normalizedSchedule: DayConfig[] = [];
  const seenOffsets = new Set<number>();
  if (Array.isArray(rawSchedule)) {
    for (const item of rawSchedule) {
      const key = String(item).trim().toLowerCase();
      const config = DAY_CONFIG_MAP[key];
      if (config && !seenOffsets.has(config.offsetFromMonday)) {
        seenOffsets.add(config.offsetFromMonday);
        normalizedSchedule.push(config);
      }
    }
  }
  normalizedSchedule.sort((a, b) => a.offsetFromMonday - b.offsetFromMonday);

  // 4. Resolve days per week
  const daysPerWeek =
    options?.daysPerWeek ||
    (normalizedSchedule.length > 0 ? normalizedSchedule.length : 0) ||
    (sortedApiDays.length > 0 ? Math.ceil(sortedApiDays.length / totalWeeks) : 0) ||
    4;

  // If normalizedSchedule is empty, fill with default days based on daysPerWeek
  if (normalizedSchedule.length === 0) {
    const offsets = DEFAULT_OFFSETS_BY_DAYS_COUNT[daysPerWeek] || DEFAULT_OFFSETS_BY_DAYS_COUNT[4];
    offsets.forEach((off) => normalizedSchedule.push(DEFAULT_CONFIGS[off]));
  }

  // 5. Resolve reminder time
  const rawReminder =
    options?.reminderTime ??
    (rawData as Record<string, unknown>).howOftenTrainReminder ??
    rawDraft?.progress?.how_often_train_reminder?.[0]?.value ??
    rawDraft?.progress?.howOftenTrainReminder?.[0]?.value ??
    'morning';
  const reminderStr = String(rawReminder).toLowerCase();
  let defaultSelectTime = '08:00';
  let defaultHour = 8;
  let defaultMinute = 0;
  if (reminderStr.includes('lunch') || reminderStr.includes('12')) {
    defaultSelectTime = '12:00';
    defaultHour = 12;
    defaultMinute = 0;
  } else if (reminderStr.includes('evening') || reminderStr.includes('18')) {
    defaultSelectTime = '18:00';
    defaultHour = 18;
    defaultMinute = 0;
  }

  // 6. Base date for calculation
  const baseDate = rawData.createTime ? new Date(rawData.createTime) : new Date();
  const startMonday = getStartMonday(baseDate);

  const mapDurationMove = (ex: ExerciseData): ApiDurationMove => {
    if (ex.isRest) {
      const match = (ex.restDuration || '').match(/(\d+)/);
      const duration = match ? parseInt(match[1], 10) : 30;
      return {
        title: 'Rest',
        duration,
      };
    }
    const rawRest = ex.restAfterExercise || ex.restBetweenSets || '0s';
    const match = String(rawRest).match(/\d+/);
    const cleanRest = match ? match[0] : '0';

    return {
      title: ex.name,
      duration: ex.duration || 30,
      ...(cleanRest !== '0' ? { rest: cleanRest } : {}),
    };
  };

  const mapWorkoutMove = (ex: ExerciseData): ApiWorkoutMove => {
    if (ex.isRest) {
      const match = (ex.restDuration || '').match(/(\d+)/);
      const duration = match ? parseInt(match[1], 10) : 30;
      return {
        title: 'Rest',
        list: [
          {
            reps: '0',
            rest: `${duration}`,
            weight: '0.0',
            time: `${duration}`,
          },
        ],
      };
    }

    const rawRest = ex.restAfterExercise || ex.restBetweenSets || '0s';
    const match = String(rawRest).match(/\d+/);
    const cleanRest = match ? match[0] : '0';

    const list: ApiWorkoutMoveSet[] = Array.from({ length: ex.sets || 1 }).map(() => {
      const setObj: ApiWorkoutMoveSet = {
        reps: ex.reps ? ex.reps.toString() : '15',
        rest: cleanRest,
        weight: '0.0',
      };
      if (ex.metricType === 'duration' && ex.duration) {
        setObj.time = ex.duration.toString();
      }
      return setObj;
    });

    return {
      title: ex.name,
      list,
    };
  };

  const newDatetimeList: FinalDatetimeListItem[] = [];

  for (let weekNum = 1; weekNum <= totalWeeks; weekNum++) {
    const weekWorkouts = programData[weekNum];

    for (let workoutNum = 1; workoutNum <= daysPerWeek; workoutNum++) {
      const dayIndex = (weekNum - 1) * daysPerWeek + (workoutNum - 1);
      const apiDay = sortedApiDays.length > dayIndex ? sortedApiDays[dayIndex] : undefined;

      const dayConfig = normalizedSchedule[(workoutNum - 1) % normalizedSchedule.length];
      const workoutDate = new Date(startMonday);
      workoutDate.setDate(startMonday.getDate() + (weekNum - 1) * 7 + dayConfig.offsetFromMonday);

      const y = workoutDate.getFullYear();
      const m = workoutDate.getMonth() + 1;
      const d = workoutDate.getDate();
      const ymd = `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;

      const sectionData = weekWorkouts?.[workoutNum];

      let warmUpMoves: ApiDurationMove[] = [];
      let moves: ApiWorkoutMove[] = [];
      let coolDownMoves: ApiDurationMove[] = [];

      if (sectionData) {
        warmUpMoves = (sectionData.warmup || []).map(mapDurationMove);
        moves = (sectionData.main || []).map(mapWorkoutMove);
        coolDownMoves = (sectionData.cooldown || []).map(mapDurationMove);
      } else if (apiDay) {
        warmUpMoves = apiDay.warmUpMoves || [];
        moves = apiDay.moves || [];
        coolDownMoves = apiDay.coolDownMoves || [];
      }

      const selectTime = apiDay?.selectTime || defaultSelectTime;
      const hour =
        apiDay?.hour !== undefined
          ? apiDay.hour
          : apiDay?.selectTime
            ? parseInt(apiDay.selectTime.split(':')[0], 10)
            : defaultHour;
      const minute =
        apiDay?.minute !== undefined
          ? apiDay.minute
          : apiDay?.selectTime
            ? parseInt(apiDay.selectTime.split(':')[1], 10)
            : defaultMinute;

      const newItem: FinalDatetimeListItem = {
        workoutType: apiDay?.workoutType || rawData.workoutType || 'programme',
        ymd: apiDay?.ymd || ymd,
        year: apiDay?.year || y,
        month: apiDay?.month || m,
        day: apiDay?.day || String(d),
        weekday: apiDay?.weekday || dayConfig.name,
        weekday2: apiDay?.weekday2 || dayConfig.shortName,
        weekdayNum: apiDay?.weekdayNum || dayConfig.dayNum,
        selectTime,
        hour,
        minute,
        warmUpMoves,
        moves,
        coolDownMoves,
      };

      newDatetimeList.push(newItem);
    }
  }

  const isManually =
    options?.isManually ??
    ((rawData as Record<string, unknown>).isManually as boolean | undefined) ??
    false;
  const isAIGenerate = isManually
    ? false
    : rawData.isAIGenerate !== undefined
      ? Boolean(rawData.isAIGenerate)
      : true;

  return {
    createTime: rawData.createTime || new Date().toISOString(),
    duration: String(totalWeeks),
    focus_area: rawData.area || rawData.focus_area || '',
    isAIGenerate,
    isManually,
    isActive: rawData.isActive !== undefined ? String(rawData.isActive) : '0',
    programmeName: rawData.programmeName || '',
    updateTime: new Date().toISOString(),
    workoutType: rawData.workoutType || 'programme',
    datetimeList: newDatetimeList,
    ...(rawDraft ? { draft: rawDraft as unknown as FinalProgrammePlan['draft'] } : {}),
  };
};
